/* eslint-disable react/no-did-update-set-state */
import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty, isNil } from 'lodash';

import ShippingStepMessage from '../ShippingStepMessage';
import ShippingStepSelection from '../shipping-step-selection/ShippingStepSelection';
import ShippingStepDetailsForm from '../ShippingStepDetailsForm';
import ShippingStepPhoneForm from '../ShippingStepPhoneForm';
import ActionButton from 'components/buttons/action-button/ActionButton';
import Spinner from 'components/spinner/Spinner';
import TutorialCarousel from 'components/tutorial-carousel/TutorialCarousel';
import PromptExit from 'libs/router/prompt-exit/PromptExit';
import { ModalFrame, ModalFrameHeader, ModalFrameInner } from 'components/modal';
import BusinessBankDetails from '../business-bank-details/BusinessBankDetails';
import CampaignAcceptanceStep from '../campaign-acceptance-step/CampaignAcceptanceStep';

import {
  deleteShippingAddress,
  fetchShipping,
  saveShippingAddress,
  saveSubcampaignShipping
} from '../../reducers/shipping';
import { putOfferState } from 'modules/subcampaigns/reducers/offer';
import { fetchSubcampaign } from 'modules/subcampaigns/reducers/subcampaign';
import { fetchOrganizationSettings } from 'modules/organizations/reducers/organizationSettings';
import { saveInfluencerAudienceData } from 'modules/influencers/reducers/influencerProfile';

import { __t, applyTranslations } from 'utils/language';
import { getInfluencerTutorialSlides } from 'utils/tutorials';
import { getDisplayDate } from 'utils/date';
import { getAllInfluecerAssetsData, getInfluencersDeliverableChannels } from 'utils/influencers';
import { SUBCAMPAIGN_STATE_ACCEPTED, shouldAskForShippingAddress } from 'utils/subcampaign';

applyTranslations(require('./shipping-modal.i18n.json'));

import type {
  Account,
  ChannelAnalyticsScreenShotData,
  Deliverable,
  Organization,
  OrganizationSettings,
  ShippingAddress,
  Subcampaign,
  SubcampaignOfferState
} from 'types';

type OwnProps = {
  subcampaign: Subcampaign,
  onCloseModal: () => void
};

type StateProps = {
  settingsId: number,
  organizationId: number,
  currentOrganization: Organization,
  list: Array<ShippingAddress>,
  isLoading: boolean,
  currentAccount: Account,
  organizationSettings: OrganizationSettings,
  offerDeliverables: {
    deliverablesByOfferId: {
      [x: number]: Array<Deliverable>
    }
  }
};

type DispatchProps = {
  dispatchFetchShipping: (organizationId: number) => Promise<void>,
  dispatchSaveShippingAddress: (organizationId: number, shippingAddress: ShippingAddress) => Promise<void>,
  dispatchDeleteShippingAddress: (organizationId: number, shippingAddress: ShippingAddress) => Promise<void>,
  dispatchPutOfferState: (offerId: number, subcampaignOfferState: SubcampaignOfferState) => Promise<void>,
  dispatchSaveSubcampaignShipping: (subcampaignId: number, shippingAddress: Partial<ShippingAddress>) => Promise<void>,
  dispatchFetchSubcampaign: (subcampaignId: number) => Promise<void>,
  dispatchSaveInfluencerAudienceData: (profileId: number, channelAnalyticsScreenShotData: Array<Partial<ChannelAnalyticsScreenShotData>>) => Promise<void>,
  dispatchFetchOrganizationSettings: (id: number) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  email: string | null,
  step: string,
  prevStep: string | null,
  editAddress: ShippingAddress | undefined | null,
  selectedAddressId: number | undefined | null,
  phone: string,
  userFinishedCheckin: boolean,
  channels: Array<{
    type: string,
    assets?: Array<ChannelAnalyticsScreenShotData>
  }>
};

const STEP_INIT = 'init';
const STEP_ADDRESS_SELECTION = 'address-selection';
const STEP_ADDRESS_DETAIL = 'address-detail';
const STEP_ADDRESS_PHONE = 'address-phone';
const STEP_BUSINESS_BANK_DETAILS = 'business-bank-details';
const STEP_READY = 'address-ready';
const STEP_FINISHED = 'campaign-accepted';
const STEP_TUTORIAL = 'tutorial';
const STEP_BUSINESS_DETAILS = 'business-details';

const STEP_MIN_HEIGHT = 440;

const PREV_STEPS = {
  [STEP_ADDRESS_PHONE]: STEP_BUSINESS_BANK_DETAILS,
  [STEP_ADDRESS_SELECTION]: STEP_ADDRESS_PHONE,
  [STEP_READY]: [STEP_ADDRESS_SELECTION]
};

class ShippingModal extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = this.getInitialState();
  }

  getInitialState(): State {
    const { currentAccount, offerDeliverables, subcampaign } = this.props;
    const deliverables = offerDeliverables?.deliverablesByOfferId ? offerDeliverables.deliverablesByOfferId[subcampaign.offer.id] : [];

    return {
      step: STEP_INIT,
      prevStep: null,
      editAddress: null,
      email: null,
      selectedAddressId: null,
      phone: currentAccount.phone || '',
      userFinishedCheckin: false,
      channels: getInfluencersDeliverableChannels(deliverables)
    };
  }

  componentDidMount(): void {
    const { dispatchFetchShipping, organizationId } = this.props;

    dispatchFetchShipping(organizationId);
  }

  componentDidUpdate(prevProps: Props): void {
    const { list } = this.props;

    if (prevProps.list.length && list.length > prevProps.list.length) {
      this.setState(prevState => ({
        ...prevState,
        selectedAddressId: list[list.length - 1].id
      }));
    }
  }

  componentWillUnmount(): void {
    const { dispatchFetchSubcampaign, subcampaign } = this.props;
    const { userFinishedCheckin } = this.state;

    if (userFinishedCheckin) {
      dispatchFetchSubcampaign(subcampaign.id);
    }
  }

  handleChangePhone = (phone: string): void => {
    this.setState({
      phone
    });
  };

  handleSaveAddress = (address?: ShippingAddress | null): void => {
    const { dispatchSaveShippingAddress, organizationId } = this.props;

    if (address) {
      dispatchSaveShippingAddress(organizationId, address);
    }

    this.handleAddressSelection();
  };

  handleSelectAddressId = (addressId?: number | null): void => {
    if (addressId) {
      this.setState({
        selectedAddressId: addressId
      });
    }
  };

  handleAddressSelection = (): void => {
    this.setState({
      step: STEP_ADDRESS_SELECTION
    });
  };

  handleEditAddress = (address: ShippingAddress): void => {
    this.setState({
      step: STEP_ADDRESS_DETAIL,
      editAddress: address
    });
  };

  handleAddAddress = (): void => {
    this.setState({
      step: STEP_ADDRESS_DETAIL,
      editAddress: null
    });
  };

  handleConfirmPhone = (address: ShippingAddress, email: string | null = null): void => {
    this.setState((prevState => ({
      editAddress: address,
      email: email || prevState.email
    })), this.handleSwitchToNextStep);
  };

  handleReady = (): void => {
    this.setState({
      step: STEP_READY
    });
  };

  handleDeleteAddress = (address?: ShippingAddress | null): void => {
    const { organizationId, dispatchDeleteShippingAddress, list } = this.props;

    if (address) {
      dispatchDeleteShippingAddress(organizationId, address);
    }

    // always reset to the first address in the list
    this.setState({
      selectedAddressId: list.filter(a => a.id !== address?.id)[0].id
    });

    this.handleAddressSelection();
  };

  handleFinish = (): void => {
    const {
      list,
      subcampaign,
      organizationId,
      dispatchPutOfferState,
      dispatchSaveSubcampaignShipping,
      dispatchSaveInfluencerAudienceData
    } = this.props;
    const { selectedAddressId, phone, email, channels } = this.state;

    const assets = getAllInfluecerAssetsData(channels);

    dispatchSaveInfluencerAudienceData(subcampaign.creatorProfile.id, assets);

    const address = list.find(a => a.id === selectedAddressId) || {};
    const showShippingStep = shouldAskForShippingAddress(subcampaign);

    const canAccept = showShippingStep
      ? !isEmpty(address) && email && phone
      : phone;

    if (canAccept) {
      const addressWithPhoneAndEmail = { ...address, phone, organizationId, email: email ?? '' };
      const offerId = subcampaign.updatedOffer && subcampaign.updatedOffer.id || subcampaign.offer.id;

      dispatchPutOfferState(offerId, { state: SUBCAMPAIGN_STATE_ACCEPTED })
        .then(() => dispatchSaveSubcampaignShipping(subcampaign.id, addressWithPhoneAndEmail))
        .then(() => {
          this.setState({
            userFinishedCheckin: true
          });

          this.handleSwitchToNextStep();
        });
    }
  };

  handleChangeAudienceData = (assets: Array<ChannelAnalyticsScreenShotData>, channelType: string): void => {
    const { channels } = this.state;

    const updatedChannels = channels.map(channel => {
      if (channel.type === channelType) {
        return {
          ...channel,
          assets
        };
      }

      return channel;
    });

    this.setState((prevState) => ({
      ...prevState,
      channels: updatedChannels
    }));
  };

  handleSettingsComplete = () => {
    const { settingsId, dispatchFetchOrganizationSettings } = this.props;

    dispatchFetchOrganizationSettings(settingsId).then(() => this.handleSwitchToNextStep());
  };

  handleSwitchToNextStep = () => {
    const { step } = this.state;
    const { list, subcampaign, onCloseModal } = this.props;
    let nextStep = STEP_BUSINESS_BANK_DETAILS;

    switch (step) {
      case STEP_INIT:
        nextStep = STEP_BUSINESS_BANK_DETAILS;
        break;
      case STEP_BUSINESS_BANK_DETAILS:
        nextStep = STEP_ADDRESS_PHONE;
        break;
      case STEP_ADDRESS_PHONE:
        if (shouldAskForShippingAddress(subcampaign)) {
          nextStep = list.length > 0 ? STEP_ADDRESS_SELECTION : STEP_ADDRESS_DETAIL;
        } else {
          nextStep = STEP_READY;
        }
        break;
      case STEP_ADDRESS_DETAIL:
        nextStep = STEP_ADDRESS_SELECTION;
        break;
      case STEP_ADDRESS_SELECTION:
        nextStep = STEP_READY;
        break;
      case STEP_FINISHED:
        nextStep = STEP_TUTORIAL;
        break;
      case STEP_READY:
        nextStep = STEP_FINISHED;

        break;
      default:
        onCloseModal();
        break;
    }

    this.setState({ step: nextStep, prevStep: step });
  };

  handleBack = () => {
    const { prevStep } = this.state;

    this.setState({
      step: prevStep || STEP_BUSINESS_BANK_DETAILS,
      prevStep: prevStep ? PREV_STEPS[prevStep] : null

    });
  };

  handleEditBusinessSettings = () => this.setState({ step: STEP_BUSINESS_DETAILS });

  renderStep(): React.ReactNode {
    const {
      settingsId,
      subcampaign,
      list,
      organizationSettings,
      currentOrganization,
      onCloseModal
    } = this.props;

    const {
      step,
      editAddress,
      selectedAddressId,
      phone,
      email } = this.state;
    const campaign = (subcampaign && subcampaign.campaign) || {};
    const endDate = getDisplayDate(campaign.endDate);
    const brandName = subcampaign.campaign.brandProfile.name;

    let componentStep: React.ReactNode = null;

    switch (step) {
      case STEP_INIT:
        componentStep = (
          <React.Fragment>
            <ModalFrameHeader
              title={__t('shipping_modal.label_check_details_validation')}
            />
            <ShippingStepMessage
              minHeight={STEP_MIN_HEIGHT}
              body={{
                main: __t('shipping_modal.init_sub_header_message'),
                sub: __t('shipping_modal.init_sub_message')
              }}
              actions={[
                <ActionButton
                  key="button-cancel"
                  onClick={onCloseModal}
                >
                  {__t('shipping_modal.buttons.accept_later')}
                </ActionButton>,
                <ActionButton
                  key="button-ok"
                  color="primary"
                  onClick={this.handleSwitchToNextStep}
                >
                  {__t('shipping_modal.buttons.yes_review')}
                </ActionButton>
              ]}
            />
          </React.Fragment>
        );
        break;
      case STEP_BUSINESS_BANK_DETAILS:
        componentStep = (
          <BusinessBankDetails
            key="businessBankDetails"
            minHeight={STEP_MIN_HEIGHT}
            settingsId={settingsId}
            organizationSettings={organizationSettings}
            subcampaignId={subcampaign.id}
            isTalentAgency={
              currentOrganization.type === 'influencer'
              && !isNil(currentOrganization.agencyOrganizationId)
            }
            onContinue={this.handleSwitchToNextStep}
            onCloseModal={onCloseModal}
          />
        );
        break;
      case STEP_READY:
        componentStep = (
          <React.Fragment>
            <ModalFrameHeader
              title={__t('shipping_modal.campaign_acceptance')}
            />
            <ShippingStepMessage
              minHeight={STEP_MIN_HEIGHT}
              body={{
                main: __t('shipping_modal.campaign_acceptance_title'),
                sub: __t('shipping_modal.campaign_acceptance_message',
                  {
                    brandName: (brandName || '').slice(-1) === 's' ? brandName : `${brandName}'s`,
                    campaignName: subcampaign.campaign.name
                  })
              }}
              actions={[
                <ActionButton
                  color="tertiary"
                  onClick={this.handleBack}
                  key="button-back"
                >
                  {__t('shipping_modal.buttons.back')}
                </ActionButton>,
                <ActionButton
                  color="primary"
                  onClick={this.handleFinish}
                  key="button-accept"
                >
                  {__t('shipping_modal.buttons.accept')}
                </ActionButton>
              ]}
            />
          </React.Fragment>
        );
        break;
      case STEP_ADDRESS_SELECTION:
        componentStep = (
          <ShippingStepSelection
            minHeight={STEP_MIN_HEIGHT}
            list={list}
            email={email}
            selectedAddressId={selectedAddressId}
            onSelectAddressId={this.handleSelectAddressId}
            onEditAddress={this.handleEditAddress}
            onAddNewAddress={this.handleAddAddress}
            onSave={this.handleConfirmPhone}
            onBack={this.handleBack}
          />
        );
        break;
      case STEP_ADDRESS_DETAIL:
        componentStep = (
          <React.Fragment>
            <ModalFrameHeader
              title={editAddress ?
                __t('shipping_modal.edit_shipping_address') : __t('shipping_modal.add_shipping_address')}
            />
            <ShippingStepDetailsForm
              allowDelete={list.length > 1 && !isNil(editAddress && editAddress.id)}
              address={editAddress}
              organizationSettings={organizationSettings}
              cancelButtonLabel={
                list.length > 0 ? __t('shipping_modal.buttons.cancel') : __t('shipping_modal.buttons.back')
              }
              onCancel={list.length > 0 ? this.handleAddressSelection : this.handleBack}
              onSave={this.handleSaveAddress}
              onDelete={this.handleDeleteAddress}
              submitButtonColor="primary"
            />
          </React.Fragment>
        );
        break;
      case STEP_TUTORIAL:
        componentStep = (
          <TutorialCarousel
            slides={getInfluencerTutorialSlides(endDate)}
            showIntroSlide
            slideHeight={STEP_MIN_HEIGHT}
            onCloseModal={onCloseModal}
          />
        );
        break;
      case STEP_FINISHED:
        componentStep = (
          <CampaignAcceptanceStep
            minHeight={STEP_MIN_HEIGHT}
            campaignName={subcampaign.campaign.name}
            brandName={subcampaign.campaign.brandProfile.name}
            onClose={onCloseModal}
            onNextStep={this.handleSwitchToNextStep}
          />
        );
        break;
      case STEP_ADDRESS_PHONE:
        componentStep = (
          <React.Fragment>
            <ModalFrameHeader
              title={__t('shipping_modal.your_contact_details')}
            />
            <ShippingStepPhoneForm
              minHeight={STEP_MIN_HEIGHT}
              phone={phone}
              onBack={this.handleBack}
              onFinish={this.handleSwitchToNextStep}
              onChangePhone={this.handleChangePhone}
            />
          </React.Fragment>
        );
        break;
      default:
        break;
    }

    if (step === STEP_TUTORIAL) {
      return componentStep;
    }

    return (
      <section
        className="shipping-modal"
        data-qa="shipping-modal"
      >
        <ModalFrame>
          {componentStep}
        </ModalFrame>
      </section>
    );
  }

  render(): React.ReactNode {
    const { isLoading } = this.props;
    const { userFinishedCheckin } = this.state;

    return (
      <section>
        {
          !userFinishedCheckin &&
            <PromptExit type="modal" />
        }
        {
          isLoading ?
            <ModalFrame>
              <ModalFrameInner
                minHeight={STEP_MIN_HEIGHT}
              >
                <Spinner
                  position="inline"
                  size="small"
                  color="light"
                />
              </ModalFrameInner>
            </ModalFrame> :
            this.renderStep()
        }
      </section>
    );
  }
}

const mapStateToProps = ({
  organizations,
  shipping,
  account,
  offerDeliverables,
  organizationSettings
}): StateProps => ({
  organizationId: organizations.currentOrganizationId,
  currentOrganization: organizations.currentOrganization,
  settingsId: organizations.currentOrganization.agencyOrganizationId || organizations.currentOrganization.id,
  isLoading: shipping.isLoading,
  list: shipping.list,
  currentAccount: account.currentAccount,
  offerDeliverables,
  organizationSettings: organizationSettings.organization
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchShipping: (organizationId: number): Promise<void> => dispatch(fetchShipping(organizationId)),
  dispatchSaveShippingAddress: (organizationId: number, address: ShippingAddress): Promise<void> => dispatch(saveShippingAddress(organizationId, address)),
  dispatchDeleteShippingAddress: (organizationId: number, address: ShippingAddress): Promise<void> => dispatch(deleteShippingAddress(organizationId, address)),

  dispatchPutOfferState: (offerId: number, offerState: SubcampaignOfferState) =>
    dispatch(putOfferState(offerId, offerState)),

  dispatchSaveSubcampaignShipping: (subcampaignId: number, address: Partial<ShippingAddress>): Promise<void> => dispatch(saveSubcampaignShipping(subcampaignId, address)),
  dispatchFetchSubcampaign: (subcampaignId: number): Promise<void> => dispatch(fetchSubcampaign(subcampaignId)),
  dispatchSaveInfluencerAudienceData: (profileId: number, assets: Array<Partial<ChannelAnalyticsScreenShotData>>): Promise<void> => dispatch(saveInfluencerAudienceData(profileId, assets)),

  dispatchFetchOrganizationSettings: (id: number) => dispatch(fetchOrganizationSettings(id))
});

export default connect(mapStateToProps, mapDispatchToProps)(ShippingModal);
